import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Package, Calendar, DollarSign, 
  Plus, Edit2, Trash2, Stethoscope, MapPin, ShoppingBag, Clock,
  Tag, Percent, Heart, Camera, Image as ImageIcon, X, Megaphone, CalendarCheck, ExternalLink,
  Receipt, CheckCircle, XCircle, Award, Settings, Lock, Eye, EyeOff
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [zones, setZones] = useState([]);
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [banners, setBanners] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [productModal, setProductModal] = useState(false);
  const [zoneModal, setZoneModal] = useState(false);
  const [doctorModal, setDoctorModal] = useState(false);
  const [serviceModal, setServiceModal] = useState(false);
  const [promoModal, setPromoModal] = useState(false);
  const [bannerModal, setBannerModal] = useState(false);
  const [heroSlideModal, setHeroSlideModal] = useState(false);
  
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [editingPromo, setEditingPromo] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingHeroSlide, setEditingHeroSlide] = useState(null);
  const [addPointsModal, setAddPointsModal] = useState(false);
  const [selectedUserForPoints, setSelectedUserForPoints] = useState(null);
  const [pointsToAdd, setPointsToAdd] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [viewingTicket, setViewingTicket] = useState(null);
  const [ticketReviewNotes, setTicketReviewNotes] = useState('');
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Product search and pagination
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const productsPerPage = 50;
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Filtered products based on search
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.category?.toLowerCase().includes(productSearch.toLowerCase())
  );
  
  // Forms
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category: '', stock: '',
    image_url: '', requires_prescription: false, discount_percent: ''
  });
  const [zoneForm, setZoneForm] = useState({ name: '', shipping_cost: '' });
  const [doctorForm, setDoctorForm] = useState({
    specialty: '', consultation_fee: '', bio: '', image_url: '',
    schedule_start: '08:00', schedule_end: '20:00',
    break_start: '14:00', break_end: '15:00'
  });
  const [serviceForm, setServiceForm] = useState({
    name: '', description: '', price: '', price_variable: false, category: 'Procedimientos'
  });
  const [promoForm, setPromoForm] = useState({
    name: '', description: '', discount_type: 'percentage', discount_value: '',
    applies_to: 'all', target_id: '', is_active: true
  });
  const [bannerForm, setBannerForm] = useState({
    title: '', subtitle: '', background_color: '#2563EB', text_color: '#FFFFFF',
    link_url: '', link_text: '', order: 0, is_active: true
  });
  const [heroSlideForm, setHeroSlideForm] = useState({
    image_url: '', title: '', subtitle: '',
    button_primary_text: 'Ver Más', button_primary_link: '/productos',
    button_secondary_text: '', button_secondary_link: '',
    order: 0, is_active: true
  });

  // Check URL params for calendar connection status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      toast.success('¡Google Calendar conectado exitosamente!');
      window.history.replaceState({}, '', '/admin');
    }
    if (params.get('calendar_error')) {
      toast.error(`Error al conectar: ${params.get('calendar_error')}`);
      window.history.replaceState({}, '', '/admin');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, user]);

  const fetchData = async () => {
    try {
      const [statsRes, productsRes, usersRes, doctorsRes, zonesRes, ordersRes, servicesRes, promosRes, catsRes, bannersRes, calendarRes, heroSlidesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products?limit=1000`),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/zones`),
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services?active_only=false`),
        axios.get(`${API}/promotions`),
        axios.get(`${API}/products/categories`),
        axios.get(`${API}/banners?active_only=false`),
        axios.get(`${API}/oauth/calendar/status`),
        axios.get(`${API}/hero-slides?active_only=false`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setUsers(usersRes.data);
      setDoctors(doctorsRes.data);
      setZones(zonesRes.data);
      setOrders(ordersRes.data);
      setServices(servicesRes.data);
      setPromotions(promosRes.data);
      setCategories(catsRes.data);
      setBanners(bannersRes.data);
      setCalendarStatus(calendarRes.data);
      setHeroSlides(heroSlidesRes.data || []);
      
      // Fetch pending tickets
      try {
        const ticketsRes = await axios.get(`${API}/admin/tickets?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingTickets(ticketsRes.data || []);
      } catch (e) {
        setPendingTickets([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Google Calendar functions
  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/oauth/calendar/login`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Error al conectar con Google Calendar');
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('¿Desconectar Google Calendar?')) return;
    try {
      await axios.delete(`${API}/oauth/calendar/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarStatus({ connected: false });
      toast.success('Google Calendar desconectado');
    } catch (error) {
      toast.error('Error al desconectar');
    }
  };

  // Product functions
  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        category: product.category,
        stock: product.stock.toString(),
        image_url: product.image_url || '',
        requires_prescription: product.requires_prescription,
        discount_percent: product.discount_percent?.toString() || ''
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '', description: '', price: '', category: '', stock: '',
        image_url: '', requires_prescription: false, discount_percent: ''
      });
    }
    setProductModal(true);
  };

  const saveProduct = async () => {
    if (!productForm.name || !productForm.price || !productForm.category) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    const data = {
      name: productForm.name,
      description: productForm.description,
      price: parseFloat(productForm.price),
      category: productForm.category,
      stock: parseInt(productForm.stock) || 0,
      image_url: productForm.image_url && productForm.image_url.trim() !== '' ? productForm.image_url.trim() : null,
      requires_prescription: productForm.requires_prescription,
      discount_percent: productForm.discount_percent ? parseFloat(productForm.discount_percent) : null
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Producto creado');
      }
      setProductModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Producto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Service functions
  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        description: service.description || '',
        price: service.price.toString(),
        price_variable: service.price_variable,
        category: service.category
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: '', description: '', price: '', price_variable: false, category: 'Procedimientos'
      });
    }
    setServiceModal(true);
  };

  const saveService = async () => {
    if (!serviceForm.name || !serviceForm.price) {
      toast.error('Completa nombre y precio');
      return;
    }

    const data = {
      name: serviceForm.name,
      description: serviceForm.description || null,
      price: parseFloat(serviceForm.price),
      price_variable: serviceForm.price_variable,
      category: serviceForm.category
    };

    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Servicio actualizado');
      } else {
        await axios.post(`${API}/services`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Servicio creado');
      }
      setServiceModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('¿Eliminar este servicio?')) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Servicio eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Promotion functions
  const openPromoModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setPromoForm({
        name: promo.name,
        description: promo.description || '',
        discount_type: promo.discount_type,
        discount_value: promo.discount_value.toString(),
        applies_to: promo.applies_to,
        target_id: promo.target_id || '',
        is_active: promo.is_active
      });
    } else {
      setEditingPromo(null);
      setPromoForm({
        name: '', description: '', discount_type: 'percentage', discount_value: '',
        applies_to: 'all', target_id: '', is_active: true
      });
    }
    setPromoModal(true);
  };

  const savePromotion = async () => {
    if (!promoForm.name || !promoForm.discount_value) {
      toast.error('Completa nombre y valor del descuento');
      return;
    }

    const data = {
      name: promoForm.name,
      description: promoForm.description || null,
      discount_type: promoForm.discount_type,
      discount_value: parseFloat(promoForm.discount_value),
      applies_to: promoForm.applies_to,
      target_id: promoForm.target_id || null,
      is_active: promoForm.is_active
    };

    try {
      if (editingPromo) {
        await axios.put(`${API}/promotions/${editingPromo.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promoción actualizada');
      } else {
        await axios.post(`${API}/promotions`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promoción creada');
      }
      setPromoModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const deletePromotion = async (promoId) => {
    if (!window.confirm('¿Eliminar esta promoción?')) return;
    try {
      await axios.delete(`${API}/promotions/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promoción eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Doctor functions
  const openDoctorModal = (doctor) => {
    setEditingDoctor(doctor);
    setDoctorForm({
      specialty: doctor.specialty || '',
      consultation_fee: doctor.consultation_fee?.toString() || '50',
      bio: doctor.bio || '',
      image_url: doctor.image_url || '',
      schedule_start: doctor.schedule_start || '08:00',
      schedule_end: doctor.schedule_end || '20:00',
      break_start: doctor.break_start || '14:00',
      break_end: doctor.break_end || '15:00'
    });
    setDoctorModal(true);
  };

  const saveDoctor = async () => {
    try {
      await axios.put(`${API}/doctors/${editingDoctor.id}`, {
        specialty: doctorForm.specialty,
        consultation_fee: parseFloat(doctorForm.consultation_fee) || 50,
        bio: doctorForm.bio || null,
        image_url: doctorForm.image_url || null,
        schedule_start: doctorForm.schedule_start,
        schedule_end: doctorForm.schedule_end,
        break_start: doctorForm.break_start,
        break_end: doctorForm.break_end
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Doctor actualizado');
      setDoctorModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  // Zone functions
  const saveZone = async () => {
    if (!zoneForm.name || !zoneForm.shipping_cost) {
      toast.error('Completa todos los campos');
      return;
    }
    try {
      await axios.post(`${API}/zones`, {
        name: zoneForm.name,
        shipping_cost: parseFloat(zoneForm.shipping_cost)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zona creada');
      setZoneModal(false);
      setZoneForm({ name: '', shipping_cost: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear zona');
    }
  };

  // Banner functions
  const openBannerModal = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setBannerForm({
        title: banner.title,
        subtitle: banner.subtitle || '',
        background_color: banner.background_color,
        text_color: banner.text_color,
        link_url: banner.link_url || '',
        link_text: banner.link_text || '',
        order: banner.order,
        is_active: banner.is_active
      });
    } else {
      setEditingBanner(null);
      setBannerForm({
        title: '', subtitle: '', background_color: '#2563EB', text_color: '#FFFFFF',
        link_url: '', link_text: '', order: banners.length, is_active: true
      });
    }
    setBannerModal(true);
  };

  const saveBanner = async () => {
    if (!bannerForm.title) {
      toast.error('El título es obligatorio');
      return;
    }

    const data = {
      title: bannerForm.title,
      subtitle: bannerForm.subtitle || null,
      background_color: bannerForm.background_color,
      text_color: bannerForm.text_color,
      link_url: bannerForm.link_url || null,
      link_text: bannerForm.link_text || null,
      order: parseInt(bannerForm.order) || 0,
      is_active: bannerForm.is_active
    };

    try {
      if (editingBanner) {
        await axios.put(`${API}/banners/${editingBanner.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner actualizado');
      } else {
        await axios.post(`${API}/banners`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner creado');
      }
      setBannerModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteBanner = async (bannerId) => {
    if (!window.confirm('¿Eliminar este banner?')) return;
    try {
      await axios.delete(`${API}/banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Hero Slides functions
  const openHeroSlideModal = (slide = null) => {
    if (slide) {
      setEditingHeroSlide(slide);
      setHeroSlideForm({
        image_url: slide.image_url,
        title: slide.title,
        subtitle: slide.subtitle || '',
        button_primary_text: slide.button_primary_text,
        button_primary_link: slide.button_primary_link,
        button_secondary_text: slide.button_secondary_text || '',
        button_secondary_link: slide.button_secondary_link || '',
        order: slide.order,
        is_active: slide.is_active
      });
    } else {
      setEditingHeroSlide(null);
      setHeroSlideForm({
        image_url: '', title: '', subtitle: '',
        button_primary_text: 'Ver Más', button_primary_link: '/productos',
        button_secondary_text: '', button_secondary_link: '',
        order: heroSlides.length, is_active: true
      });
    }
    setHeroSlideModal(true);
  };

  const saveHeroSlide = async () => {
    if (!heroSlideForm.image_url || !heroSlideForm.title) {
      toast.error('La imagen y el título son obligatorios');
      return;
    }

    const data = {
      image_url: heroSlideForm.image_url,
      title: heroSlideForm.title,
      subtitle: heroSlideForm.subtitle || null,
      button_primary_text: heroSlideForm.button_primary_text,
      button_primary_link: heroSlideForm.button_primary_link,
      button_secondary_text: heroSlideForm.button_secondary_text || null,
      button_secondary_link: heroSlideForm.button_secondary_link || null,
      order: parseInt(heroSlideForm.order) || 0,
      is_active: heroSlideForm.is_active
    };

    try {
      if (editingHeroSlide) {
        await axios.put(`${API}/hero-slides/${editingHeroSlide.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Foto actualizada');
      } else {
        await axios.post(`${API}/hero-slides`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Foto agregada');
      }
      setHeroSlideModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteHeroSlide = async (slideId) => {
    if (!window.confirm('¿Eliminar esta foto del inicio?')) return;
    try {
      await axios.delete(`${API}/hero-slides/${slideId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Foto eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Ticket review functions
  const reviewTicket = async (ticketId, status) => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/review`, {
        status: status,
        admin_notes: ticketReviewNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (status === 'approved') {
        toast.success('Ticket aprobado. Puntos agregados al cliente.');
      } else {
        toast.success('Ticket rechazado.');
      }
      
      setViewingTicket(null);
      setTicketReviewNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al revisar ticket');
    }
  };

  // Manual points functions
  const openAddPointsModal = (user) => {
    setSelectedUserForPoints(user);
    setPointsToAdd('');
    setPointsReason('');
    setAddPointsModal(true);
  };

  const addPointsToUser = async () => {
    if (!pointsToAdd || !pointsReason) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      await axios.post(`${API}/admin/points/add`, {
        user_id: selectedUserForPoints.id,
        points: parseInt(pointsToAdd),
        reason: pointsReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Se agregaron ${pointsToAdd} puntos`);
      setAddPointsModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar puntos');
    }
  };

  const deductPointsFromUser = async () => {
    if (!pointsToAdd || !pointsReason) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      await axios.post(`${API}/admin/points/deduct`, {
        user_id: selectedUserForPoints.id,
        points: parseInt(pointsToAdd),
        reason: pointsReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Se descontaron ${pointsToAdd} puntos`);
      setAddPointsModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al descontar puntos');
    }
  };

  // Change password function
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Completa todos los campos');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('¡Contraseña actualizada exitosamente!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setChangingPassword(false);
    }
  };

  // Order functions
  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${role}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rol actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      paid: 'bg-blue-100 text-blue-700',
      preparing: 'bg-purple-100 text-purple-700',
      shipped: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (!isAuthenticated || user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container-app">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Panel de Administración</h1>
          <p className="text-slate-500">J3A Farmacias y Consultorios</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div 
            className="card-dashboard cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab('usuarios')}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                <p className="text-sm text-slate-500">Usuarios</p>
              </div>
            </div>
          </div>
          <div 
            className="card-dashboard cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/contabilidad')}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${stats?.total_revenue?.toFixed(0) || '0'}</p>
                <p className="text-sm text-slate-500">Ingresos</p>
              </div>
            </div>
          </div>
          <div 
            className="card-dashboard cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab('productos')}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{products.length}</p>
                <p className="text-sm text-slate-500">Productos</p>
              </div>
            </div>
          </div>
          <div 
            className="card-dashboard cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/citas')}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.today_appointments || 0}</p>
                <p className="text-sm text-slate-500">Citas Hoy</p>
              </div>
            </div>
          </div>
        </div>

        {/* Google Calendar Connection */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                calendarStatus.connected ? 'bg-green-100' : 'bg-slate-100'
              }`}>
                <CalendarCheck className={`w-5 h-5 ${
                  calendarStatus.connected ? 'text-green-600' : 'text-slate-400'
                }`} />
              </div>
              <div>
                <p className="font-medium text-slate-900">Google Calendar</p>
                {calendarStatus.connected ? (
                  <p className="text-sm text-green-600">
                    Conectado: {calendarStatus.email}
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">
                    Las citas se agregarán automáticamente al calendario
                  </p>
                )}
              </div>
            </div>
            {calendarStatus.connected ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={disconnectGoogleCalendar}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                Desconectar
              </Button>
            ) : (
              <Button 
                onClick={connectGoogleCalendar}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Conectar Calendar
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl flex-wrap">
            <TabsTrigger value="products" className="rounded-lg">
              <Package className="w-4 h-4 mr-2" />
              Productos
            </TabsTrigger>
            <TabsTrigger value="services" className="rounded-lg">
              <Heart className="w-4 h-4 mr-2" />
              Servicios
            </TabsTrigger>
            <TabsTrigger value="promotions" className="rounded-lg">
              <Percent className="w-4 h-4 mr-2" />
              Promociones
            </TabsTrigger>
            <TabsTrigger value="orders" className="rounded-lg">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Pedidos
            </TabsTrigger>
            <TabsTrigger value="doctors" className="rounded-lg">
              <Stethoscope className="w-4 h-4 mr-2" />
              Doctores
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger value="zones" className="rounded-lg">
              <MapPin className="w-4 h-4 mr-2" />
              Zonas
            </TabsTrigger>
            <TabsTrigger value="banners" className="rounded-lg">
              <Megaphone className="w-4 h-4 mr-2" />
              Banners
            </TabsTrigger>
            <TabsTrigger value="hero-slides" className="rounded-lg">
              <ImageIcon className="w-4 h-4 mr-2" />
              Fotos Inicio
            </TabsTrigger>
            <TabsTrigger value="tickets" className="rounded-lg">
              <Receipt className="w-4 h-4 mr-2" />
              Tickets {pendingTickets.length > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-1.5 rounded-full">{pendingTickets.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg">
              <Settings className="w-4 h-4 mr-2" />
              Config
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h2 className="text-lg font-semibold">Productos ({products.length})</h2>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Input
                  placeholder="Buscar producto..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setProductPage(1);
                  }}
                  className="w-full sm:w-64"
                />
                <Button onClick={() => openProductModal()} className="btn-primary whitespace-nowrap">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar
                </Button>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Producto</th>
                    <th className="text-left p-4 font-medium text-slate-600">Categoría</th>
                    <th className="text-left p-4 font-medium text-slate-600">Precio</th>
                    <th className="text-left p-4 font-medium text-slate-600">Stock</th>
                    <th className="text-left p-4 font-medium text-slate-600">Descuento</th>
                    <th className="text-left p-4 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.slice((productPage - 1) * productsPerPage, productPage * productsPerPage).map((product) => (
                    <tr key={product.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <button 
                            onClick={() => openProductModal(product)}
                            className="relative group"
                            title="Clic para editar imagen"
                          >
                            <img 
                              src={product.image_url || 'https://via.placeholder.com/40?text=Med'} 
                              alt={product.name}
                              className="w-12 h-12 rounded-lg object-cover bg-slate-100 group-hover:opacity-70 transition-opacity"
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <Camera className="w-5 h-5 text-slate-700" />
                            </div>
                          </button>
                          <span className="font-medium text-slate-900 truncate max-w-[200px]">{product.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">{product.category}</td>
                      <td className="p-4 font-medium">${product.price.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={product.stock > 0 ? 'text-green-600' : 'text-red-600'}>
                          {product.stock}
                        </span>
                      </td>
                      <td className="p-4">
                        {product.discount_percent ? (
                          <span className="text-[#E52225] font-medium">-{product.discount_percent}%</span>
                        ) : '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openProductModal(product)} title="Editar producto">
                            <Edit2 className="w-4 h-4 text-slate-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteProduct(product.id)} title="Eliminar producto">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination */}
              {filteredProducts.length > productsPerPage && (
                <div className="p-4 border-t flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Mostrando {((productPage - 1) * productsPerPage) + 1}-{Math.min(productPage * productsPerPage, filteredProducts.length)} de {filteredProducts.length} productos
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setProductPage(p => Math.max(1, p - 1))}
                      disabled={productPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-sm text-slate-600">
                      Página {productPage} de {Math.ceil(filteredProducts.length / productsPerPage)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setProductPage(p => Math.min(Math.ceil(filteredProducts.length / productsPerPage), p + 1))}
                      disabled={productPage >= Math.ceil(filteredProducts.length / productsPerPage)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Servicios y Procedimientos ({services.length})</h2>
              <Button onClick={() => openServiceModal()} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Servicio
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service) => (
                <div key={service.id} className="card-dashboard">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{service.name}</h3>
                      <p className="text-sm text-slate-500 mt-1">{service.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-lg font-bold text-[#2563EB]">${service.price.toFixed(2)}</span>
                        {service.price_variable && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">a variación</span>
                        )}
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded mt-2 inline-block">
                        {service.category}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openServiceModal(service)}>
                        <Edit2 className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteService(service.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promotions">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Promociones ({promotions.length})</h2>
              <Button onClick={() => openPromoModal()} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Promoción
              </Button>
            </div>
            
            {promotions.length === 0 ? (
              <div className="card-dashboard text-center py-12">
                <Tag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin promociones</h3>
                <p className="text-slate-500 mb-4">Crea tu primera promoción para atraer clientes</p>
                <Button onClick={() => openPromoModal()} className="btn-secondary">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Promoción
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {promotions.map((promo) => (
                  <div key={promo.id} className={`card-dashboard border-l-4 ${promo.is_active ? 'border-green-500' : 'border-slate-300'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{promo.name}</h3>
                          {promo.is_active ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Activa</span>
                          ) : (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Inactiva</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{promo.description}</p>
                        <div className="mt-2">
                          <span className="text-2xl font-bold text-[#E52225]">
                            {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`}
                          </span>
                          <span className="text-sm text-slate-500 ml-2">
                            {promo.applies_to === 'all' ? 'En todo' : 
                             promo.applies_to === 'category' ? `En ${promo.target_id}` : 'Producto específico'}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openPromoModal(promo)}>
                          <Edit2 className="w-4 h-4 text-slate-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deletePromotion(promo.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <h2 className="text-lg font-semibold mb-4">Pedidos Recientes</h2>
            <div className="space-y-3">
              {orders.slice(0, 20).map((order) => (
                <div key={order.id} className="card-dashboard">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Pedido #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-slate-500">{order.delivery_type === 'pickup' ? 'Recoger en tienda' : 'Envío'}</p>
                      <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleString('es-MX')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">${order.total.toFixed(2)}</p>
                      <Select value={order.status} onValueChange={(v) => updateOrderStatus(order.id, v)}>
                        <SelectTrigger className={`w-32 h-8 text-xs ${getStatusColor(order.status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pendiente</SelectItem>
                          <SelectItem value="paid">Pagado</SelectItem>
                          <SelectItem value="preparing">Preparando</SelectItem>
                          <SelectItem value="shipped">Enviado</SelectItem>
                          <SelectItem value="delivered">Entregado</SelectItem>
                          <SelectItem value="cancelled">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Doctors Tab */}
          <TabsContent value="doctors">
            <h2 className="text-lg font-semibold mb-4">Doctores ({doctors.length})</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {doctors.map((doctor) => (
                <div key={doctor.id} className="card-dashboard">
                  <div className="flex items-start gap-4">
                    <img
                      src={doctor.image_url || 'https://via.placeholder.com/80?text=Dr'}
                      alt={doctor.name}
                      className="w-20 h-20 rounded-xl object-cover bg-slate-100"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">{doctor.name}</h3>
                        <Button variant="ghost" size="icon" onClick={() => openDoctorModal(doctor)}>
                          <Edit2 className="w-4 h-4 text-slate-600" />
                        </Button>
                      </div>
                      <p className="text-sm text-[#2563EB]">{doctor.specialty}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {doctor.schedule_start || '08:00'} - {doctor.schedule_end || '20:00'}
                        </span>
                        <span>Consulta: ${doctor.consultation_fee?.toFixed(2)}</span>
                      </div>
                      <div className={`inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs ${
                        doctor.is_available ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${doctor.is_available ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {doctor.is_available ? 'Disponible' : 'No disponible'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <h2 className="text-lg font-semibold mb-4">Usuarios ({users.length})</h2>
            <div className="bg-white rounded-2xl overflow-hidden overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Usuario</th>
                    <th className="text-left p-4 font-medium text-slate-600">Email</th>
                    <th className="text-left p-4 font-medium text-slate-600">Rol</th>
                    <th className="text-left p-4 font-medium text-slate-600">Puntos</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100">
                      <td className="p-4 font-medium text-slate-900">{u.name}</td>
                      <td className="p-4 text-slate-600">{u.email}</td>
                      <td className="p-4">
                        <Select value={u.role} onValueChange={(v) => updateUserRole(u.id, v)}>
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cliente">Cliente</SelectItem>
                            <SelectItem value="doctor">Doctor</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 text-slate-600">{u.points || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Zones Tab */}
          <TabsContent value="zones">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Zonas de Envío ({zones.length})</h2>
              <Button onClick={() => setZoneModal(true)} className="btn-secondary">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Zona
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {zones.map((zone) => (
                <div key={zone.id} className="card-dashboard">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#2563EB] rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{zone.name}</p>
                      <p className="text-[#E52225] font-bold">${zone.shipping_cost.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Banners Tab */}
          <TabsContent value="banners">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Banners Promocionales ({banners.length})</h2>
                <p className="text-sm text-slate-500">Se muestran en la página de inicio</p>
              </div>
              <Button onClick={() => openBannerModal()} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Banner
              </Button>
            </div>
            <div className="space-y-4">
              {banners.map((banner) => (
                <div 
                  key={banner.id} 
                  className="rounded-xl overflow-hidden shadow-sm border"
                >
                  {/* Banner Preview */}
                  <div 
                    className="px-6 py-4 flex items-center justify-between"
                    style={{ 
                      backgroundColor: banner.background_color,
                      color: banner.text_color 
                    }}
                  >
                    <div>
                      <h3 className="text-xl font-bold">{banner.title}</h3>
                      {banner.subtitle && <p className="opacity-90">{banner.subtitle}</p>}
                    </div>
                    {banner.link_text && (
                      <span className="px-4 py-2 bg-white text-slate-900 rounded-lg text-sm font-medium">
                        {banner.link_text}
                      </span>
                    )}
                  </div>
                  {/* Banner Controls */}
                  <div className="px-4 py-3 bg-white flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span>Orden: {banner.order}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        banner.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {banner.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                      {banner.link_url && (
                        <span className="text-slate-400">→ {banner.link_url}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openBannerModal(banner)}>
                        <Edit2 className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteBanner(banner.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              
              {banners.length === 0 && (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay banners creados</p>
                  <Button onClick={() => openBannerModal()} variant="outline" className="mt-4">
                    Crear primer banner
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Hero Slides Tab - Fotos de Inicio */}
          <TabsContent value="hero-slides">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Fotos de Inicio ({heroSlides.length})</h2>
                <p className="text-sm text-slate-500">Estas fotos aparecen en pantalla completa en la página principal</p>
              </div>
              <Button onClick={() => openHeroSlideModal()} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Foto
              </Button>
            </div>
            
            <div className="grid gap-4">
              {heroSlides.length > 0 ? (
                heroSlides.map((slide) => (
                  <div 
                    key={slide.id} 
                    className={`bg-white rounded-xl p-4 border ${slide.is_active ? 'border-green-200' : 'border-slate-200 opacity-60'}`}
                  >
                    <div className="flex gap-4">
                      <div className="w-48 h-28 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={slide.image_url} 
                          alt={slide.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{slide.title}</h3>
                            <p className="text-slate-500 text-sm">{slide.subtitle}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {slide.is_active ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Activo</span>
                            ) : (
                              <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs">Inactivo</span>
                            )}
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Orden: {slide.order}</span>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2 text-sm text-slate-500">
                          <span className="bg-slate-100 px-2 py-1 rounded">{slide.button_primary_text} → {slide.button_primary_link}</span>
                          {slide.button_secondary_text && (
                            <span className="bg-slate-100 px-2 py-1 rounded">{slide.button_secondary_text} → {slide.button_secondary_link}</span>
                          )}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button variant="ghost" size="sm" onClick={() => openHeroSlideModal(slide)}>
                            <Edit2 className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteHeroSlide(slide.id)}>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <ImageIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No hay fotos de inicio configuradas</p>
                  <Button onClick={() => openHeroSlideModal()} variant="outline">
                    Agregar primera foto
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tickets Tab - Verificar Tickets de Clientes */}
          <TabsContent value="tickets">
            <div className="mb-6">
              <h2 className="text-lg font-semibold">Tickets Pendientes ({pendingTickets.length})</h2>
              <p className="text-sm text-slate-500">Revisa y aprueba los tickets de compra de los clientes para otorgar puntos</p>
            </div>
            
            {pendingTickets.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No hay tickets pendientes</p>
                <p className="text-slate-500 text-sm">Todos los tickets han sido revisados</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {pendingTickets.map((ticket) => (
                  <div key={ticket.id} className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm">
                    <div className="flex gap-4">
                      <div 
                        className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer"
                        onClick={() => setViewingTicket(ticket)}
                      >
                        <img 
                          src={ticket.ticket_image} 
                          alt="Ticket"
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-lg">{ticket.user_name}</p>
                            <p className="text-sm text-slate-500">{ticket.user_email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-600">${ticket.amount.toFixed(2)}</p>
                            <p className="text-sm text-slate-500">= {ticket.points_to_add} puntos</p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(ticket.created_at).toLocaleString('es-MX')}
                        </p>
                        {ticket.notes && (
                          <p className="text-sm text-slate-600 mt-2 italic">"{ticket.notes}"</p>
                        )}
                        
                        <div className="flex gap-2 mt-4">
                          <Button 
                            onClick={() => reviewTicket(ticket.id, 'approved')}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Aprobar (+{ticket.points_to_add} pts)
                          </Button>
                          <Button 
                            onClick={() => setViewingTicket(ticket)}
                            variant="outline"
                          >
                            Ver Detalle
                          </Button>
                          <Button 
                            onClick={() => {
                              setViewingTicket(ticket);
                              setTicketReviewNotes('');
                            }}
                            variant="outline"
                            className="text-red-500 border-red-200 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Rechazar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Agregar Puntos Manualmente */}
            <div className="mt-8 pt-6 border-t">
              <h3 className="font-semibold text-lg mb-4">Gestionar Puntos de Usuarios</h3>
              <p className="text-sm text-slate-500 mb-4">Selecciona un usuario de la lista de Usuarios para agregar o descontar puntos manualmente</p>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {users.filter(u => u.role === 'cliente').slice(0, 9).map((u) => (
                  <div key={u.id} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{u.first_name} {u.last_name}</p>
                      <p className="text-xs text-slate-500">{u.email}</p>
                      <p className="text-xs font-semibold text-green-600">{u.points || 0} puntos</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openAddPointsModal(u)}>
                      <Award className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-md">
              <h2 className="text-lg font-semibold mb-6">Configuración de la Cuenta</h2>
              
              {/* Change Password Section */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Lock className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Cambiar Contraseña</h3>
                    <p className="text-sm text-slate-500">Actualiza tu contraseña de acceso</p>
                  </div>
                </div>
                
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label>Contraseña Actual</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Nueva Contraseña</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Confirmar Nueva Contraseña</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="mt-1"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={changingPassword}
                  >
                    {changingPassword ? 'Guardando...' : 'Cambiar Contraseña'}
                  </Button>
                </form>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Product Modal */}
        <Dialog open={productModal} onOpenChange={setProductModal}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Image Preview and Edit */}
              <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="relative group">
                  {productForm.image_url ? (
                    <img 
                      src={productForm.image_url} 
                      alt="Vista previa"
                      className="w-32 h-32 rounded-xl object-cover bg-white shadow-sm"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/128?text=Error';
                      }}
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-xl bg-white border-2 border-dashed border-slate-300 flex items-center justify-center">
                      <ImageIcon className="w-10 h-10 text-slate-400" />
                    </div>
                  )}
                  {productForm.image_url && (
                    <button
                      type="button"
                      onClick={() => setProductForm({ ...productForm, image_url: '' })}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      title="Quitar imagen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Upload from gallery */}
                <div className="flex gap-2 w-full">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('La imagen debe ser menor a 5MB');
                          return;
                        }
                        setUploadingImage(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const response = await axios.post(`${API}/upload/image`, formData, {
                            headers: { 
                              Authorization: `Bearer ${token}`,
                              'Content-Type': 'multipart/form-data'
                            }
                          });
                          setProductForm({ ...productForm, image_url: response.data.url });
                          toast.success('Imagen subida correctamente');
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Error al subir la imagen');
                        } finally {
                          setUploadingImage(false);
                        }
                      }
                    }}
                    className="hidden"
                    id="product-image-upload"
                    disabled={uploadingImage}
                  />
                  <label
                    htmlFor="product-image-upload"
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg transition-colors ${uploadingImage ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:bg-slate-50'}`}
                  >
                    {uploadingImage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-slate-600">Subiendo...</span>
                      </>
                    ) : (
                      <>
                        <Camera className="w-4 h-4 text-slate-600" />
                        <span className="text-sm text-slate-600">Subir desde galería</span>
                      </>
                    )}
                  </label>
                </div>
                
                {/* Or use URL */}
                <div className="w-full">
                  <Label className="text-sm text-slate-600">O pega una URL de imagen</Label>
                  <Input
                    value={productForm.image_url || ''}
                    onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                    placeholder="https://ejemplo.com/imagen.jpg"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Nombre *</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Precio *</Label>
                  <Input
                    type="number"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Categoría *</Label>
                  <Input
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="mt-1"
                    list="categories"
                  />
                  <datalist id="categories">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Stock</Label>
                  <Input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Descuento (%)</Label>
                  <Input
                    type="number"
                    value={productForm.discount_percent}
                    onChange={(e) => setProductForm({ ...productForm, discount_percent: e.target.value })}
                    placeholder="Ej: 10, 25"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                <Label className="cursor-pointer">Requiere Receta Médica</Label>
                <Switch
                  checked={productForm.requires_prescription}
                  onCheckedChange={(v) => setProductForm({ ...productForm, requires_prescription: v })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setProductModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={saveProduct} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Service Modal */}
        <Dialog open={serviceModal} onOpenChange={setServiceModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Editar Servicio' : 'Nuevo Servicio'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre del Servicio *</Label>
                <Input
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  placeholder="Ej: Consulta Médica, Inyección, etc."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Precio *</Label>
                  <Input
                    type="number"
                    value={serviceForm.price}
                    onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={serviceForm.category} onValueChange={(v) => setServiceForm({ ...serviceForm, category: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Consultas">Consultas</SelectItem>
                      <SelectItem value="Procedimientos">Procedimientos</SelectItem>
                      <SelectItem value="Análisis">Análisis</SelectItem>
                      <SelectItem value="Certificados">Certificados</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Precio Variable (a variación)</Label>
                <Switch
                  checked={serviceForm.price_variable}
                  onCheckedChange={(v) => setServiceForm({ ...serviceForm, price_variable: v })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setServiceModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={saveService} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Promotion Modal */}
        <Dialog open={promoModal} onOpenChange={setPromoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingPromo ? 'Editar Promoción' : 'Nueva Promoción'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre de la Promoción *</Label>
                <Input
                  value={promoForm.name}
                  onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
                  placeholder="Ej: Lunes 25% descuento"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={promoForm.description}
                  onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Descuento</Label>
                  <Select value={promoForm.discount_type} onValueChange={(v) => setPromoForm({ ...promoForm, discount_type: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Porcentaje %</SelectItem>
                      <SelectItem value="fixed">Monto Fijo $</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor *</Label>
                  <Input
                    type="number"
                    value={promoForm.discount_value}
                    onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })}
                    placeholder={promoForm.discount_type === 'percentage' ? 'Ej: 25' : 'Ej: 50'}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Aplica a</Label>
                <Select value={promoForm.applies_to} onValueChange={(v) => setPromoForm({ ...promoForm, applies_to: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los productos</SelectItem>
                    <SelectItem value="category">Categoría específica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {promoForm.applies_to === 'category' && (
                <div>
                  <Label>Categoría</Label>
                  <Select value={promoForm.target_id} onValueChange={(v) => setPromoForm({ ...promoForm, target_id: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecciona categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Promoción Activa</Label>
                <Switch
                  checked={promoForm.is_active}
                  onCheckedChange={(v) => setPromoForm({ ...promoForm, is_active: v })}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setPromoModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={savePromotion} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Doctor Modal */}
        <Dialog open={doctorModal} onOpenChange={setDoctorModal}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Doctor: {editingDoctor?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Image Preview */}
              <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <div className="relative group">
                  {doctorForm.image_url ? (
                    <img 
                      src={doctorForm.image_url} 
                      alt="Foto del doctor"
                      className="w-24 h-24 rounded-full object-cover bg-white shadow-sm"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/96?text=Dr';
                      }}
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-white border-2 border-dashed border-slate-300 flex items-center justify-center">
                      <Stethoscope className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  {doctorForm.image_url && (
                    <button
                      type="button"
                      onClick={() => setDoctorForm({ ...doctorForm, image_url: '' })}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      title="Quitar imagen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="w-full">
                  <Label className="text-sm text-slate-600">URL de la Foto</Label>
                  <Input
                    value={doctorForm.image_url}
                    onChange={(e) => setDoctorForm({ ...doctorForm, image_url: e.target.value })}
                    placeholder="Pega aquí el link de la foto..."
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Especialidad</Label>
                <Input
                  value={doctorForm.specialty}
                  onChange={(e) => setDoctorForm({ ...doctorForm, specialty: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Costo de Consulta ($)</Label>
                <Input
                  type="number"
                  value={doctorForm.consultation_fee}
                  onChange={(e) => setDoctorForm({ ...doctorForm, consultation_fee: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Biografía</Label>
                <Textarea
                  value={doctorForm.bio}
                  onChange={(e) => setDoctorForm({ ...doctorForm, bio: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div className="border-t pt-4">
                <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4" />
                  Horario de Atención
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Hora Inicio</Label>
                    <Input
                      type="time"
                      value={doctorForm.schedule_start}
                      onChange={(e) => setDoctorForm({ ...doctorForm, schedule_start: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Hora Fin</Label>
                    <Input
                      type="time"
                      value={doctorForm.schedule_end}
                      onChange={(e) => setDoctorForm({ ...doctorForm, schedule_end: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-sm">Descanso Inicio</Label>
                    <Input
                      type="time"
                      value={doctorForm.break_start}
                      onChange={(e) => setDoctorForm({ ...doctorForm, break_start: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Descanso Fin</Label>
                    <Input
                      type="time"
                      value={doctorForm.break_end}
                      onChange={(e) => setDoctorForm({ ...doctorForm, break_end: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setDoctorModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={saveDoctor} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Zone Modal */}
        <Dialog open={zoneModal} onOpenChange={setZoneModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nueva Zona de Envío</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre de la Zona</Label>
                <Input
                  value={zoneForm.name}
                  onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                  placeholder="Ej: Centro, Norte"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Costo de Envío ($)</Label>
                <Input
                  type="number"
                  value={zoneForm.shipping_cost}
                  onChange={(e) => setZoneForm({ ...zoneForm, shipping_cost: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setZoneModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={saveZone} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Banner Modal */}
        <Dialog open={bannerModal} onOpenChange={setBannerModal}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBanner ? 'Editar Banner' : 'Nuevo Banner'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Banner Preview */}
              <div 
                className="rounded-xl px-6 py-5 text-center"
                style={{ 
                  backgroundColor: bannerForm.background_color,
                  color: bannerForm.text_color 
                }}
              >
                <h3 className="text-xl font-bold">{bannerForm.title || 'Título del Banner'}</h3>
                {bannerForm.subtitle && <p className="opacity-90 mt-1">{bannerForm.subtitle}</p>}
                {bannerForm.link_text && (
                  <span className="inline-block mt-3 px-4 py-2 bg-white text-slate-900 rounded-lg text-sm font-medium">
                    {bannerForm.link_text}
                  </span>
                )}
              </div>

              <div>
                <Label>Título *</Label>
                <Input
                  value={bannerForm.title}
                  onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                  placeholder="Ej: ¡Consulta GRATIS los Miércoles!"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Subtítulo</Label>
                <Input
                  value={bannerForm.subtitle}
                  onChange={(e) => setBannerForm({ ...bannerForm, subtitle: e.target.value })}
                  placeholder="Ej: Consultas presenciales sin costo"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Color de Fondo</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={bannerForm.background_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, background_color: e.target.value })}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={bannerForm.background_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, background_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Color de Texto</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={bannerForm.text_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, text_color: e.target.value })}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={bannerForm.text_color}
                      onChange={(e) => setBannerForm({ ...bannerForm, text_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Link (opcional)</Label>
                  <Input
                    value={bannerForm.link_url}
                    onChange={(e) => setBannerForm({ ...bannerForm, link_url: e.target.value })}
                    placeholder="/productos o /citas"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Texto del Botón</Label>
                  <Input
                    value={bannerForm.link_text}
                    onChange={(e) => setBannerForm({ ...bannerForm, link_text: e.target.value })}
                    placeholder="Ej: Ver Más"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Orden (número)</Label>
                  <Input
                    type="number"
                    value={bannerForm.order}
                    onChange={(e) => setBannerForm({ ...bannerForm, order: e.target.value })}
                    className="mt-1"
                    min="0"
                  />
                </div>
                <div className="flex items-center justify-between pt-6">
                  <Label className="cursor-pointer">Activo</Label>
                  <Switch
                    checked={bannerForm.is_active}
                    onCheckedChange={(v) => setBannerForm({ ...bannerForm, is_active: v })}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setBannerModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={saveBanner} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Hero Slide Modal */}
        <Dialog open={heroSlideModal} onOpenChange={setHeroSlideModal}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingHeroSlide ? 'Editar Foto de Inicio' : 'Nueva Foto de Inicio'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Preview */}
              {heroSlideForm.image_url && (
                <div className="relative rounded-xl overflow-hidden aspect-video bg-slate-900">
                  <img 
                    src={heroSlideForm.image_url} 
                    alt="Vista previa"
                    className="w-full h-full object-cover opacity-70"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-4">
                    <h3 className="text-2xl font-bold">{heroSlideForm.title || 'Título'}</h3>
                    <p className="text-lg opacity-90">{heroSlideForm.subtitle || 'Subtítulo'}</p>
                    <div className="flex gap-2 mt-3">
                      <span className="px-4 py-2 bg-red-500 rounded-lg text-sm">{heroSlideForm.button_primary_text}</span>
                      {heroSlideForm.button_secondary_text && (
                        <span className="px-4 py-2 bg-white text-slate-900 rounded-lg text-sm">{heroSlideForm.button_secondary_text}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label>URL de la Imagen *</Label>
                <Input
                  value={heroSlideForm.image_url}
                  onChange={(e) => setHeroSlideForm({ ...heroSlideForm, image_url: e.target.value })}
                  placeholder="https://ejemplo.com/imagen.jpg"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Pega aquí el link de tu imagen</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Título *</Label>
                  <Input
                    value={heroSlideForm.title}
                    onChange={(e) => setHeroSlideForm({ ...heroSlideForm, title: e.target.value })}
                    placeholder="Ej: J3A Farmacias"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Subtítulo</Label>
                  <Input
                    value={heroSlideForm.subtitle}
                    onChange={(e) => setHeroSlideForm({ ...heroSlideForm, subtitle: e.target.value })}
                    placeholder="Ej: Más baratos que similar"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Texto Botón Primario</Label>
                  <Input
                    value={heroSlideForm.button_primary_text}
                    onChange={(e) => setHeroSlideForm({ ...heroSlideForm, button_primary_text: e.target.value })}
                    placeholder="Ej: Ver Productos"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Link Botón Primario</Label>
                  <Input
                    value={heroSlideForm.button_primary_link}
                    onChange={(e) => setHeroSlideForm({ ...heroSlideForm, button_primary_link: e.target.value })}
                    placeholder="/productos"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Texto Botón Secundario</Label>
                  <Input
                    value={heroSlideForm.button_secondary_text}
                    onChange={(e) => setHeroSlideForm({ ...heroSlideForm, button_secondary_text: e.target.value })}
                    placeholder="Ej: Agendar Cita"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Link Botón Secundario</Label>
                  <Input
                    value={heroSlideForm.button_secondary_link}
                    onChange={(e) => setHeroSlideForm({ ...heroSlideForm, button_secondary_link: e.target.value })}
                    placeholder="/citas"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Orden (número)</Label>
                  <Input
                    type="number"
                    value={heroSlideForm.order}
                    onChange={(e) => setHeroSlideForm({ ...heroSlideForm, order: e.target.value })}
                    className="mt-1"
                    min="0"
                  />
                </div>
                <div className="flex items-center justify-between pt-6">
                  <Label className="cursor-pointer">Activo</Label>
                  <Switch
                    checked={heroSlideForm.is_active}
                    onCheckedChange={(v) => setHeroSlideForm({ ...heroSlideForm, is_active: v })}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setHeroSlideModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={saveHeroSlide} className="flex-1 btn-primary">
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Ticket Modal */}
        <Dialog open={!!viewingTicket} onOpenChange={() => setViewingTicket(null)}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Revisar Ticket</DialogTitle>
            </DialogHeader>
            {viewingTicket && (
              <div className="space-y-4">
                <img
                  src={viewingTicket.ticket_image}
                  alt="Ticket"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Cliente</p>
                    <p className="font-medium">{viewingTicket.user_name}</p>
                    <p className="text-sm text-slate-500">{viewingTicket.user_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Monto</p>
                    <p className="text-2xl font-bold text-green-600">${viewingTicket.amount.toFixed(2)}</p>
                    <p className="text-sm text-slate-500">= {viewingTicket.points_to_add} puntos</p>
                  </div>
                </div>
                {viewingTicket.notes && (
                  <p className="text-slate-600 italic">"{viewingTicket.notes}"</p>
                )}
                
                <div>
                  <Label>Notas del Admin (opcional)</Label>
                  <Input
                    value={ticketReviewNotes}
                    onChange={(e) => setTicketReviewNotes(e.target.value)}
                    placeholder="Ej: Ticket verificado correctamente"
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={() => reviewTicket(viewingTicket.id, 'rejected')}
                    variant="outline"
                    className="flex-1 text-red-500 border-red-200 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rechazar
                  </Button>
                  <Button 
                    onClick={() => reviewTicket(viewingTicket.id, 'approved')}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aprobar (+{viewingTicket.points_to_add} pts)
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add/Deduct Points Modal */}
        <Dialog open={addPointsModal} onOpenChange={setAddPointsModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Gestionar Puntos</DialogTitle>
            </DialogHeader>
            {selectedUserForPoints && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="font-medium">{selectedUserForPoints.first_name} {selectedUserForPoints.last_name}</p>
                  <p className="text-sm text-slate-500">{selectedUserForPoints.email}</p>
                  <p className="text-lg font-bold text-green-600 mt-2">
                    Puntos actuales: {selectedUserForPoints.points || 0}
                  </p>
                </div>

                <div>
                  <Label>Cantidad de Puntos</Label>
                  <Input
                    type="number"
                    value={pointsToAdd}
                    onChange={(e) => setPointsToAdd(e.target.value)}
                    placeholder="Ej: 50"
                    className="mt-1"
                    min="1"
                  />
                </div>

                <div>
                  <Label>Razón *</Label>
                  <Input
                    value={pointsReason}
                    onChange={(e) => setPointsReason(e.target.value)}
                    placeholder="Ej: Compra en tienda, Promoción especial, Canje de puntos"
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={deductPointsFromUser}
                    variant="outline"
                    className="flex-1 text-red-500 border-red-200 hover:bg-red-50"
                    disabled={!pointsToAdd || !pointsReason}
                  >
                    Descontar Puntos
                  </Button>
                  <Button 
                    onClick={addPointsToUser}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    disabled={!pointsToAdd || !pointsReason}
                  >
                    Agregar Puntos
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
